import type { RouteObject } from "react-router";
import { createBrowserRouter, Navigate } from "react-router";

import { Layout } from "~/app/layout/layout.tsx";
import { SidebarLayout } from "~/app/layout/sidebar-layout.tsx";
import { AccountSettingsPage } from "~/pages/account-settings/index.tsx";
import { AddressBookPage } from "~/pages/address-book-page/index.tsx";
import { AdminPage } from "~/pages/admin-page/index.tsx";
import { AuthRedirect } from "~/pages/auth/auth-redirect.tsx";
import { DeepLinkValidator } from "~/pages/auth/deep-link-validator.tsx";
import { AutoDispatchCustomerPage } from "~/pages/auto-dispatch/customer-page.tsx";
import { AutoDispatchGlobalPage } from "~/pages/auto-dispatch/global-page.tsx";
import { CommoditiesPage } from "~/pages/commodities-page/index.tsx";
import { DashboardPage } from "~/pages/dashboard-page/index.tsx";
import { FTLQuotingPage } from "~/pages/ftl-quoting-page/ftl-quoting-page.tsx";
import { FtlShipmentPage } from "~/pages/ftl-shipment-page/ftl-shipment-page.tsx";
import { LoadsPage } from "~/pages/loads/index.tsx";
import { LtlShipmentPage } from "~/pages/ltl-shipment-page/ltl-shipment-page.tsx";
import { MySettingsPage } from "~/pages/my-settings-page/index.tsx";
import { NotFoundPage } from "~/pages/not-found-page/not-found-page.tsx";
import { NotificationsPage } from "~/pages/notifications/index.tsx";
import { PaymentPage } from "~/pages/payment/index.tsx";
import { ProfilePage } from "~/pages/profile/index.tsx";
import { QuoteBoardPage } from "~/pages/quote-board/index.tsx";
import { QuotePage } from "~/pages/quote-page/index.tsx";
import { ShipmentReRatePage } from "~/pages/re-rate/index.tsx";
import { ReportsDetailPage } from "~/pages/reports-detail-page/index.tsx";
import { ReportsPage } from "~/pages/reports-page/index.tsx";
import { RoutingGuideCustomerPage } from "~/pages/routing-guide/customer-page.tsx";
import { RoutingGuideGlobalPage } from "~/pages/routing-guide/global-page.tsx";
import { RoutingGuidePage } from "~/pages/routing-guide/index.tsx";
import { SettingsPage } from "~/pages/settings-page/index.tsx";
import { ShipmentManagementPage } from "~/pages/shipment-management/index.tsx";
import { TemplatesPage } from "~/pages/templates-page/index.tsx";
import { TrackShipmentPage } from "~/pages/track-shipment-page/track-shipment-page.tsx";
import { VendorManagementPage } from "~/pages/vendor/index.tsx";
import { Construction } from "~/shared/components/construction/construction.tsx";
import { VendorAccessGuard } from "~/shared/components/role-guard/vendor-access-guard.tsx";
import { getBaseUrl } from "~/shared/utils/get-base-url.ts";
import { getIsInternal } from "~/shared/utils/get-deployment-type.ts";

import { routes } from "./web-routes.ts";

const constructionRoute = (path: string, message: string): RouteObject => ({
	element: <Construction message={message} />,
	path,
});

const sidebarLayoutRoute = (
	notFoundElement: RouteObject["element"],
): RouteObject => ({
	element: <SidebarLayout />,
	children: [
		{ element: <TrackShipmentPage />, path: routes.trackShipment },
		{ element: notFoundElement, path: "*" },
	],
});

export function createRouter() {
	const isInternal = getIsInternal();

	const quotePagePaths = [
		routes.quoteDetails,
		routes.quoteDetailsWithId,
		routes.carrierSelection,
		routes.shipmentDetails,
	];

	const addressBookPaths = [
		routes.addressBook,
		routes.addressBookWithCustomer,
		routes.addressBookEdit,
	];

	const vendorManagementPaths = [
		routes.vendorManagement,
		routes.vendorManagementWithCustomer,
	];

	const ltlShipmentPaths = [
		routes.ltlShipment,
		routes.ltlShipmentWithId,
		routes.ltlShipmentCarrierSelection,
	];

	const shipmentReratePaths = [routes.shipmentRerateCarrierSelection];

	const routesConfig: RouteObject[] = [
		{
			element: (
				<VendorAccessGuard>
					<Layout />
				</VendorAccessGuard>
			),
			children: [
				{
					element: isInternal ? (
						<DashboardPage />
					) : (
						<Navigate replace to={routes.track} />
					),
					path: routes.root,
				},
				...quotePagePaths.map((path) => ({
					element: <QuotePage />,
					path,
				})),
				{
					element: <AuthRedirect />,
					path: routes.authCallback,
				},
				{
					element: <LoadsPage />,
					path: routes.track,
				},
				{ element: <SettingsPage />, path: routes.settings },
				constructionRoute(
					routes.tender,
					"Tender feature is currently being developed and will be available soon.",
				),
				{
					element: isInternal ? <DashboardPage /> : <PaymentPage />,
					path: routes.payment,
				},
				...addressBookPaths.map((path) => ({
					element: <AddressBookPage />,
					path,
				})),
				...(isInternal
					? [
							{
								element: <RoutingGuidePage />,
								path: routes.routingGuides,
								children: [
									{
										index: true,
										element: <RoutingGuideGlobalPage />,
									},
									{ path: "global", element: <RoutingGuideGlobalPage /> },
									{
										path: ":customerId",
										element: <RoutingGuideCustomerPage />,
									},
								],
							},
						]
					: []),
				{
					element: <AdminPage />,
					path: routes.admin,
				},
				{
					element: <AdminPage />,
					path: routes.adminWithCustomer,
				},
				{ element: <QuoteBoardPage />, path: routes.quotes },
				{ element: <ProfilePage />, path: routes.profile },
				{ element: <CommoditiesPage />, path: routes.commodities },
				{ element: <CommoditiesPage />, path: routes.commoditiesWithCustomer },
				...(isInternal
					? [
							{
								element: <ShipmentManagementPage />,
								path: routes.shipmentManagement,
							},
						]
					: []),
				{ element: <TemplatesPage />, path: routes.templates },
				{ element: <NotificationsPage />, path: routes.notifications },
				{
					element: <NotificationsPage />,
					path: routes.notificationsWithCustomer,
				},
				{
					element: <TemplatesPage />,
					path: routes.templatesWithCustomer,
				},

				...vendorManagementPaths.map((path) => ({
					element: <VendorManagementPage />,
					path,
				})),
				{ element: <MySettingsPage />, path: routes.mySettings },
				{ element: <AccountSettingsPage />, path: routes.accountSettings },
				{ element: <ReportsPage />, path: routes.reports },
				{ element: <ReportsDetailPage />, path: routes.reportsWithId },
				{
					element: <ReportsDetailPage />,
					path: routes.reportsWithIdAndCustomer,
				},
				...(isInternal
					? [
							{
								path: routes.autoDispatch,
								children: [
									{ element: <AutoDispatchGlobalPage />, path: "global" },
									{ index: true, element: <AutoDispatchCustomerPage /> },
									{
										path: ":customerId",
										element: <AutoDispatchCustomerPage />,
									},
								],
							},
						]
					: [
							{ element: <FTLQuotingPage />, path: routes.createFTLQuote },
							{ element: <FtlShipmentPage />, path: routes.ftlShipment },
						]),
				...ltlShipmentPaths.map((path) => ({
					element: <LtlShipmentPage />,
					path,
				})),
				...shipmentReratePaths.map((path) => ({
					element: <ShipmentReRatePage />,
					path,
				})),
			],
		},
		sidebarLayoutRoute(<NotFoundPage />),
	];

	return createBrowserRouter(routesConfig, {
		basename: getBaseUrl(),
		future: {},
	});
}

export function createPublicRouter() {
	const isInternal = getIsInternal();

	return createBrowserRouter(
		[
			{
				element: <Layout />,
				children: [
					{
						element: <AuthRedirect />,
						path: routes.root,
					},
					{
						element: <AuthRedirect />,
						path: routes.authCallback,
					},
				],
			},
			// Wildcard route validates the path and handles accordingly:
			// - Invalid paths show 404 (for both internal and external users)
			// - Valid external paths redirect to sign-in (with URL preservation)
			// - Valid internal paths in public router show 404 (shouldn't happen)
			// /shipment-tracking remains public via sidebarLayoutRoute.
			sidebarLayoutRoute(<DeepLinkValidator isExternal={!isInternal} />),
		],
		{
			basename: getBaseUrl(),
			future: {},
		},
	);
}
